import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchMyActiveJobs, watchGuardJobs, updateJob, jobDisplayNumber } from "../../services/firestore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatPhone, smsHref } from "../../utils/phone";
import { hawbNumLabel } from "../../services/hawbLabel";
import { guardWorkOrder } from "../../services/guardStopOrder";
import CalendarSubscribe from "../../components/CalendarSubscribe";
import { StopPreviewSheet } from "./JobExecution";
import "./GuardLayout.css";

const STATUS_CHIP = {
  confirmed:  { label: "Ready to start",       cls: "g-chip-green" },
  inProgress: { label: "In progress",           cls: "g-chip-teal" },
  grabbed:    { label: "Awaiting confirmation", cls: "g-chip-amber" },
  complete:   { label: "Complete",              cls: "g-chip-green" },
  cancelled:  { label: "Cancelled",             cls: "g-chip-red" },
};

const TYPE_CHIP = {
  inbound:   { label: "Inbound",    cls: "g-chip-blue" },
  outbound:  { label: "Outbound",   cls: "g-chip-teal" },
  roundtrip: { label: "Round trip", cls: "g-chip-purple" },
  direct:    { label: "Direct",     cls: "g-chip-amber" },
};

// Format a scheduled date string "2026-04-22" as "Wed, Apr 22"
function formatScheduledDate(dateStr) {
  if (!dateStr) return "";
  // Parse as local date (avoid UTC shift from plain `new Date("2026-04-22")`)
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// Returns true if `dateStr` (YYYY-MM-DD) is tomorrow in the local timezone.
// Used to surface "confirm with the rep" cards for jobs scheduled tomorrow.
function isTomorrow(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return false;
  const target = new Date(y, m - 1, d);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    target.getFullYear() === tomorrow.getFullYear() &&
    target.getMonth() === tomorrow.getMonth() &&
    target.getDate() === tomorrow.getDate()
  );
}

// Build the pre-filled confirmation SMS body. Per Orion FAQ: contractor must
// text the rep by 11am the day before. Includes the route so the rep sees
// what they're confirming (origin → destination, with OUT OF TOWN flagged).
function buildConfirmationSms(job, guardName) {
  const rep = job.salesRepName || "there";
  const time = job.scheduledTime ? ` at ${job.scheduledTime}` : "";
  const dateLabel = formatScheduledDate(job.scheduledDate);
  // Build a route blurb. For direct jobs we list origin → destination (with
  // OUT OF TOWN spelled out). For inbound/outbound just mention the first
  // pickup so the rep knows where to expect us.
  const routeStops = (job.stops || []).filter((s) => s.type !== "airport");
  let routeStr = "";
  if (routeStops.length > 0) {
    const labelOf = (s) => s.outOfTown ? "OUT OF TOWN" : (s.locationName || s.address || "");
    if (routeStops.length === 1) {
      routeStr = ` — pickup at ${labelOf(routeStops[0])}`;
    } else {
      const origin = labelOf(routeStops[0]);
      const dest = labelOf(routeStops[routeStops.length - 1]);
      routeStr = ` — pickup at ${origin}, then ${dest}`;
    }
  }
  return `Hi ${rep}, this is ${guardName || "your Orion contractor"} confirming your detail ${dateLabel}${time}${routeStr}. Please confirm when you can. Thanks!`;
}

export default function MyJobs() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeJobs, setActiveJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("active");
  // Stop opened as a view-only card: { job, stop, number }.
  const [preview, setPreview] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  // Saved locations, for the contacts on a stop card (read-only).
  const [locationsById, setLocationsById] = useState({});
  useEffect(() => {
    if (!userProfile?.companyId) return;
    getDocs(query(collection(db, "locations"), where("companyId", "==", userProfile.companyId)))
      .then((snap) => {
        const m = {};
        snap.docs.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; });
        setLocationsById(m);
      })
      .catch(() => {});
  }, [userProfile?.companyId]);

  useEffect(() => {
    if (!userProfile?.id) return;
    setLoadError(null);
    const onErr = (err) => {
      // Stop the infinite spinner and show what actually went wrong so a
      // failed connection/permission/index issue is diagnosable from the
      // phone instead of hanging silently.
      setLoading(false);
      setLoadError(err);
    };
    const unsub1 = watchMyActiveJobs(userProfile.id, (data) => {
      setActiveJobs(data);
      setLoadError(null);
      setLoading(false);
    }, onErr);
    const unsub2 = watchGuardJobs(userProfile.id, (data) => {
      setAllJobs(data);
    }, onErr);
    return () => { unsub1(); unsub2(); };
  }, [userProfile]);

  // Sort by scheduledDate (soonest first), then scheduledTime; unscheduled
  // jobs sink to the bottom. Active tab shows upcoming first; All tab uses
  // the same order so completed jobs are still grouped by their date.
  function bySchedule(a, b) {
    const da = a.scheduledDate || "9999-99-99";
    const db = b.scheduledDate || "9999-99-99";
    if (da !== db) return da.localeCompare(db);
    return (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99");
  }
  const displayJobs = (tab === "active" ? activeJobs : allJobs).slice().sort(bySchedule);

  // Jobs the guard should confirm with the rep TODAY (= job is tomorrow).
  // Per Orion FAQ: text the rep by 11am the day before to confirm.
  // Filter is broker-driven (requiresRepConfirm flag); for older jobs created
  // before that flag existed, fall back to the salesRepPhone presence.
  const tomorrowsJobs = activeJobs.filter((j) => {
    if (!isTomorrow(j.scheduledDate)) return false;
    if (!j.salesRepPhone) return false;
    if (j.repConfirmedAt) return false;
    // If the broker setting is explicitly false, skip — Malca etc.
    if (j.requiresRepConfirm === false) return false;
    return true;
  });

  async function markRepConfirmed(jobId, guardName) {
    try {
      await updateJob(jobId, {
        repConfirmedAt: new Date().toISOString(),
        repConfirmedBy: guardName || "",
      });
    } catch (err) {
      console.error("Couldn't mark rep contacted", err);
    }
  }

  function formatDate(ts) {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  return (
    <div style={{ paddingBottom: 12 }}>
      <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #e8e8e8", marginBottom: 8 }}>
        {[{ key: "active", label: "Active" }, { key: "all", label: "All jobs" }].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: "14px 0", fontSize: 13, fontWeight: 600,
            border: "none", background: "none", cursor: "pointer",
            color: tab === t.key ? "#185FA5" : "#888",
            borderBottom: `2px solid ${tab === t.key ? "#185FA5" : "transparent"}`,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "active" && (
        <div style={{ margin: "8px 12px 14px" }}>
          {/* One line until tapped — the full setup is only needed once. */}
          <button
            onClick={() => setShowCalendar((v) => !v)}
            style={{
              width: "100%", textAlign: "left", background: "#fff", border: "1px solid #e3e6ea",
              borderRadius: 12, padding: "12px 14px", fontSize: 14, fontWeight: 700, color: "#185FA5", cursor: "pointer",
            }}
          >
            📅 Phone calendar sync {showCalendar ? "▲" : "›"}
          </button>
          {showCalendar && <div style={{ marginTop: 8 }}><CalendarSubscribe /></div>}
        </div>
      )}

      {/* Confirmations to text today (jobs scheduled tomorrow). Only shown on
          the Active tab. Each card has a tap-to-text button that opens the
          phone's SMS app with a pre-filled confirmation message to the rep. */}
      {tab === "active" && tomorrowsJobs.length > 0 && (
        <div style={{ margin: "8px 12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#a36b1d", textTransform: "uppercase", marginBottom: 6 }}>
            ⚠ Confirmations to send today ({tomorrowsJobs.length})
          </div>
          {tomorrowsJobs.map((job) => {
            const body = buildConfirmationSms(job, userProfile?.name);
            const smsUrl = smsHref(job.salesRepPhone, body);
            const prettyPhone = formatPhone(job.salesRepPhone);
            return (
              <div key={job.id} style={{
                background: "#fffbe9", border: "1px solid #f0c878", borderRadius: 8,
                padding: "10px 12px", marginBottom: 6,
              }}>
                <div style={{ fontSize: 12, color: "#7a5400", marginBottom: 6 }}>
                  <strong>{jobDisplayNumber(job) || "—"}</strong> · {formatScheduledDate(job.scheduledDate)}
                  {job.scheduledTime && <span> · {job.scheduledTime}</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 8 }}>
                  Text {job.salesRepName || "the rep"} to confirm
                </div>
                <a
                  href={smsUrl}
                  className="g-btn g-btn-primary"
                  style={{ display: "block", textAlign: "center", textDecoration: "none", marginBottom: 6 }}
                >
                  📱 Text {job.salesRepName || "rep"} {prettyPhone ? `· ${prettyPhone}` : ""}
                </a>
                {/* Tap once after sending the text — hides this card on every
                    device and tells the owner the confirmation was sent. */}
                <button
                  onClick={() => markRepConfirmed(job.id, userProfile?.name)}
                  style={{
                    width: "100%", padding: "8px",
                    background: "transparent", color: "#7a5400",
                    border: "1px solid #f0c878", borderRadius: 7,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  ✓ I sent the text — dismiss
                </button>
              </div>
            );
          })}
        </div>
      )}

      {loadError ? (
        <div style={{ padding: "24px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontWeight: 700, color: "#a32d2d", marginBottom: 6 }}>
            Couldn't load your jobs
          </div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
            {loadError.code ? `Code: ${loadError.code}` : ""}
          </div>
          <div style={{
            fontSize: 11, color: "#888", wordBreak: "break-word",
            background: "#f7f7f7", borderRadius: 8, padding: "8px 10px",
            margin: "0 auto 12px", maxWidth: 360, textAlign: "left",
          }}>
            {loadError.message || String(loadError)}
          </div>
          <button className="g-btn g-btn-primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      ) : loading ? (
        <div style={{ padding: "32px 16px", textAlign: "center", color: "#aaa" }}>Loading...</div>
      ) : displayJobs.length === 0 ? (
        <div className="g-empty">
          <div className="g-empty-icon">📋</div>
          <div className="g-empty-text">{tab === "active" ? "No active jobs" : "No job history"}</div>
          <div className="g-empty-sub">
            {tab === "active" ? "Grab a job from the Pool tab" : "Completed jobs will appear here"}
          </div>
        </div>
      ) : displayJobs.map((job) => {
        const st = STATUS_CHIP[job.status] || { label: job.status, cls: "g-chip-gray" };
        const tc = TYPE_CHIP[job.jobType] || { label: job.jobType, cls: "g-chip-gray" };
        const myAssignment = job.guardAssignments?.find((g) => g.guardId === userProfile?.id);
        const stops = job.stops || [];
        // Display the airport implicitly (every airport job has one) — count
        // only the non-airport stops so an outbound from one location reads
        // as "1 stop" not "2." The airport is still tracked in `stops` for
        // execution flow / billing; we just don't surface it in the count.
        const realStops = stops.filter((s) => s.type !== "airport");
        const doneStops = realStops.filter((s) => s.completedAt).length;
        const canExecute = ["confirmed","inProgress"].includes(job.status);
        const scheduled = formatScheduledDate(job.scheduledDate);

        const isDirect = job.jobType === "direct";
        const hasOOT = (stops || []).some((s) => s.outOfTown);
        return (
          <div key={job.id} className="g-card" style={{ margin: "8px 12px" }}>
            <div style={{ padding: "14px" }}>
              {/* When first, then which job. Time = the earliest stop time
                  (what the guard plans around), else the job's time. */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 15, color: "#111" }}>
                  {scheduled || `Created ${formatDate(job.createdAt)}`}
                  {(() => {
                    const t = (stops || []).map((x) => x.scheduledTime).filter(Boolean).sort()[0] || job.scheduledTime;
                    return t ? ` · ${t}` : "";
                  })()}
                </span>
                <span className={`g-chip ${st.cls}`}>{st.label}</span>
              </div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#185FA5", marginBottom: 6 }}>
                {/* Ferrari jobs go by their order numbers (FX numbers are internal). */}
                {jobDisplayNumber(job) || "—"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                <span className={`g-chip ${tc.cls}`} style={{ fontSize: 10 }}>{tc.label}</span>
                {hasOOT && (
                  <span style={{
                    background: "#fff7e0", color: "#a36b1d",
                    border: "1px solid #f0c878", borderRadius: 10,
                    padding: "1px 8px", fontSize: 10, fontWeight: 700,
                  }}>OOT</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#333", fontWeight: 500, marginBottom: 2 }}>{job.brokerName || "—"}</div>
              {job.salesRepName && (
                <div style={{ fontSize: 11, color: "#185FA5", fontWeight: 600, marginBottom: 4 }}>
                  👤 {job.salesRepName}
                </div>
              )}
              {/* Route — vertical stop list (design mockup 1). Stop names give
                  the guard a faster ID than ✈ CLT (same on every job).
                  Out-of-town destinations show as an amber OOT chip.
                  Direction matters: inbound = airport → delivery (collect at
                  CLT, drive to consignee); outbound = pickup → airport.
                  The airport row renders on the correct side accordingly.
                  Long jobs cap at 4 rows + a "+N more" line for density. */}
              {(() => {
                const routeStops = (stops || []).filter((s) => s.type !== "airport");
                if (routeStops.length === 0) return null;
                const pkgCountAt = (s) => (job.packages || []).filter((p) => p.stopId === s.id).length;
                const airportRow = (hint) => !isDirect && job.airportCode ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, background: "#EEF4FF", color: "#185FA5",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0,
                    }}>✈</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#185FA5" }}>
                      {job.airportCode}
                      <span style={{ fontWeight: 500, color: "#888", marginLeft: 6, fontSize: 11 }}>
                        {hint}
                      </span>
                    </div>
                  </div>
                ) : null;
                // Round trips touch the airport at BOTH ends — collect at the
                // top of the run, drop off at the bottom.
                const airportAbove = job.jobType === "inbound" || job.jobType === "roundtrip";
                const airportBelow = job.jobType !== "inbound";
                // Same driving order (and numbers) as the job's own screens;
                // display only. Tap a stop for its address, contacts and
                // packages.
                const stopRows = guardWorkOrder(job).map((s, i) => {
                  const n = pkgCountAt(s);
                  return (
                    <div key={i} onClick={() => setPreview({ job, stop: s, number: i + 1 })}
                      style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", cursor: "pointer" }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        background: s.completedAt ? "#2d7a2d" : "#185FA5",
                        color: "#fff", fontWeight: 800,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
                      }}>
                        {s.completedAt ? "✓" : i + 1}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>
                          {s.outOfTown ? (
                            <span style={{
                              background: "#fff7e0", color: "#a36b1d",
                              border: "1px solid #f0c878", borderRadius: 8,
                              padding: "0 5px", fontSize: 10, fontWeight: 700,
                            }}>OOT</span>
                          ) : (s.locationName || "—")}
                        </div>
                        {(s.scheduledTime || n > 0) && (
                          <div style={{ fontSize: 11, color: "#888" }}>
                            {s.scheduledTime ? `🕐 ${s.scheduledTime}` : ""}
                            {s.scheduledTime && n > 0 ? " · " : ""}
                            {n > 0 ? `${n} package${n === 1 ? "" : "s"}` : ""}
                          </div>
                        )}
                      </div>
                      <span style={{ color: "#98a4b3", fontSize: 14, alignSelf: "center" }}>›</span>
                    </div>
                  );
                });
                return (
                  <div style={{ margin: "4px 0 8px" }}>
                    {airportAbove && airportRow("collect here")}
                    {stopRows}
                    {airportBelow && airportRow("drop off")}
                  </div>
                );
              })()}
              {/* On-site contact — first stop's location contact (if
                  parsed). One-tap call from the card saves the guard a
                  trip into JobExecution just to find the phone. */}
              {(() => {
                const firstReal = (stops || []).find((s) => s.type !== "airport");
                const phone = firstReal?.contactPhone;
                const name  = firstReal?.contactName;
                if (!phone && !name) return null;
                return (
                  <div style={{ fontSize: 11, color: "#185FA5", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    📇 <span style={{ fontWeight: 600, color: "#222" }}>{name || "Contact"}</span>
                    {phone && (
                      <a
                        href={`tel:${String(phone).replace(/[^0-9+]/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "#185FA5", textDecoration: "none", fontFamily: "monospace" }}
                      >📞 {formatPhone(phone)}</a>
                    )}
                  </div>
                );
              })()}
              {/* Stop count + progress bar only on airport jobs (in/out).
                  Direct jobs are inherently A→B; a "2 stops" or "1 stop"
                  count there is noise — the guard knows to drive the
                  package over. POD photos on directs (Malca/Ferrari)
                  are handled in execution, not surfaced here. */}
              {!isDirect && realStops.length > 0 && (
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 5, background: "#e8e8e8", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${(doneStops / realStops.length) * 100}%`, height: "100%", background: "#185FA5", borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 11, color: "#888", flexShrink: 0 }}>{doneStops}/{realStops.length} stop{realStops.length === 1 ? "" : "s"}</span>
                </div>
              )}

              {canExecute && (
                <button className="g-btn g-btn-primary" style={{ fontSize: 15, minHeight: 52 }} onClick={() => navigate(`/guard/job/${job.id}`)}>
                  {/* This only opens the job — the billing clock starts on the
                      green Start button there, once the guard reaches the
                      origin. Both saying "Start job" made the second one look
                      like a duplicate, and invites tapping it from the
                      driveway. */}
                  {job.status === "inProgress" ? "Resume job →" : "Open job →"}
                </button>
              )}
              {job.status === "grabbed" && (
                <div style={{ background: "#fff3cd", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#856404", textAlign: "center" }}>
                  ⏳ Waiting for owner confirmation
                </div>
              )}
            </div>
          </div>
        );
      })}
      {preview && (
        <StopPreviewSheet
          stop={preview.stop}
          number={preview.number}
          packages={(preview.job.packages || []).filter((p) => p.stopId === preview.stop.id)}
          numLabel={hawbNumLabel(preview.job)}
          locationsById={locationsById}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
